import { useEffect, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";

// ---------------------------------------------------------------------------
// EditDocumentDialog
//
// Props:
//   open       boolean
//   onClose    () => void
//   onSaved    () => void   — called after successful save to trigger refetch
//   document   object      — the document row from LegalVault state
//                            must have: id, version_no, issue_date, expiry_date, notes
// ---------------------------------------------------------------------------
export default function EditDocumentDialog({ open, onClose, onSaved, document }) {
  const [issueDate, setIssueDate]   = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes]           = useState("");
  const [saving, setSaving]         = useState(false);
  const [conflictError, setConflictError] = useState("");

  // Pre-fill fields whenever the dialog opens with a document
  useEffect(() => {
    if (open && document) {
      setIssueDate(document.issue_date || "");
      setExpiryDate(document.expiry_date || "");
      setNotes(document.notes || "");
      setConflictError("");
    }
  }, [open, document]);

  const submit = async (e) => {
    e.preventDefault();
    if (!document) return;
    setSaving(true);
    setConflictError("");
    try {
      await api.put(`/legal/documents/${document.id}`, {
        issue_date:       issueDate  || null,
        expiry_date:      expiryDate || null,
        notes:            notes      || null,
        expected_version: document.version_no,
      });
      toast.success("Đã cập nhật tài liệu");
      onSaved();
      onClose();
    } catch (err) {
      if (err?.response?.status === 409) {
        // Version conflict — keep dialog open so user sees the inline error
        setConflictError(
          parseApiError(err, "Xung đột phiên bản — tải lại trang").message
        );
        toast.error("Xung đột phiên bản — tải lại trang");
      } else {
        toast.error(parseApiError(err, "Không cập nhật được tài liệu").message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa tài liệu</DialogTitle>
        </DialogHeader>

        {document && (
          <form onSubmit={submit} className="space-y-4 mt-2">
            {/* Document info (read-only) */}
            <div className="bg-stone-50 rounded p-3 text-sm space-y-1">
              <div className="font-medium text-stone-800 truncate">{document.file_name}</div>
              <div className="text-xs text-stone-500">Phiên bản hiện tại: v{document.version_no}</div>
            </div>

            {/* Version conflict inline error */}
            {conflictError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700"
                data-testid="edit-doc-conflict-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{conflictError}</span>
              </div>
            )}

            {/* Editable fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ngày hiệu lực</Label>
                <Input
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  data-testid="edit-doc-issue-date"
                />
              </div>
              <div>
                <Label>Ngày hết hạn</Label>
                <Input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  data-testid="edit-doc-expiry-date"
                />
              </div>
            </div>

            <div>
              <Label>Ghi chú</Label>
              <Textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Nhập ghi chú…"
                data-testid="edit-doc-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                Huỷ
              </Button>
              <Button type="submit" disabled={saving} data-testid="edit-doc-submit">
                {saving ? "Đang lưu…" : "Lưu"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
